import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
  useTheme,
  Badge
} from "@mui/material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { getBatches, getSingleOrders, getLiveOrders } from "../api/adminApi";

import DashboardIcon from "@mui/icons-material/Dashboard";
import StoreIcon from "@mui/icons-material/Store";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LabelIcon from "@mui/icons-material/Label";
import GroupIcon from "@mui/icons-material/Group";

import Sidebar from "./Sidebar";
import Header from "./Header";

// Custom pulsing animation styles for the red dot
const pulsingBadgeStyle = {
  "& .MuiBadge-badge": {
    backgroundColor: "#E85D3F",
    color: "#E85D3F",
    boxShadow: "0 0 0 1.5px #FFFFFF",
    width: "9px",
    height: "9px",
    minWidth: "9px",
    right: "2px",
    top: "2px",
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
};

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const { admin } = useContext(AuthContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeCount, setActiveCount] = useState(0);

  const checkActiveOrders = useCallback(async () => {
    try {
      const [batchRes, singleRes, liveRes] = await Promise.all([
        getBatches(),
        getSingleOrders(),
        getLiveOrders()
      ]);

      const activeBatches = (batchRes.data || []).filter(
        (b) => !["DELIVERED", "COMPLETED", "CANCELLED"].includes(b.status)
      );
      const activeSingleOrders = (singleRes.data || []).filter(
        (o) => !["DELIVERED", "CANCELLED", "REJECTED"].includes(o.status)
      );
      const activeLiveOrders = (liveRes.data || []).filter(
        (o) => !["DELIVERED", "CANCELLED", "REJECTED"].includes(o.status)
      );

      setActiveCount(activeBatches.length + activeSingleOrders.length + activeLiveOrders.length);
    } catch (err) {
      console.error("Layout poll error:", err);
    }
  }, []);

  useEffect(() => {
    checkActiveOrders();
    const interval = setInterval(checkActiveOrders, 10000);
    return () => clearInterval(interval);
  }, [checkActiveOrders]);

  const getNavValue = () => {
    const path = location.pathname;

    if (path.startsWith("/vendors")) return "vendors";
    if (path.startsWith("/batches")) return "batches";
    if (path.startsWith("/delivery-boys")) return "delivery";
    if (path.startsWith("/tags")) return "tags";
    if (path.startsWith("/riders")) return "riders";
    if (path.startsWith("/zonal-admins")) return "zonal";

    return "dashboard";
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        bgcolor: "#FFFDF8",
      }}
    >
      {/* DESKTOP SIDEBAR */}
      {!isMobile && <Sidebar />}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%"
        }}
      >
        {/* HEADER */}
        <Header />

        {/* MAIN CONTENT */}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2 },
            pb: isMobile ? "110px" : "20px",
          }}
        >
          <Outlet />
        </Box>

        {/* MODERN MOBILE BOTTOM NAV (Floating Pill Design) */}
        {isMobile && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              pb: 3,
              pointerEvents: "none",
              zIndex: 1000,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                pointerEvents: "auto",
                borderRadius: "24px",
                bgcolor: "#FFFFFF",
                border: "1px solid #ECE5DA",
                boxShadow: "0 10px 32px rgba(58, 44, 35, 0.12), 0 4px 8px rgba(58, 44, 35, 0.04)",
                overflow: "hidden",
                maxWidth: "500px",
                mx: "auto",
              }}
            >
              <BottomNavigation
                value={getNavValue()}
                showLabels
                sx={{
                  height: 70,
                  bgcolor: "transparent",
                  "& .MuiBottomNavigationAction-root": {
                    minWidth: "auto",
                    padding: "8px 2px",
                    color: "#A59D96",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                  "& .MuiBottomNavigationAction-label": {
                    fontSize: "0.65rem",
                    fontWeight: "700",
                    mt: 0.5,
                    transition: "all 0.3s ease",
                  },
                  "& .Mui-selected": {
                    color: "#3A2C23 !important",
                  },
                  "& .Mui-selected .MuiBottomNavigationAction-label": {
                    fontSize: "0.7rem",
                    fontWeight: "900",
                  },
                  "& .Mui-selected svg": {
                    bgcolor: "#F8C332",
                    color: "#3A2C23",
                    borderRadius: "12px",
                    padding: "4px",
                    fontSize: "2.1rem",
                    transform: "translateY(-4px)",
                    boxShadow: "0 4px 12px rgba(248, 195, 50, 0.4)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  },
                  "& svg": {
                    fontSize: "1.6rem",
                    padding: "4px",
                    borderRadius: "12px",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }
                }}
              >
                {/* 1. Dashboard (Common for all roles) */}
                <BottomNavigationAction value="dashboard" label="Dashboard" icon={<DashboardIcon />} onClick={() => navigate("/dashboard")} />

                {/* 🏙️ CITY ADMIN TABS */}
                {admin?.role === "CITY_ADMIN" && (
                  <BottomNavigationAction value="zonal" label="Admins" icon={<GroupIcon />} onClick={() => navigate("/zonal-admins")} />
                )}

                {/* 📍 ZONAL ADMIN TABS (Rendered individually without React Fragment warnings) */}
                {admin?.role !== "CITY_ADMIN" && (
                  <BottomNavigationAction value="tags" label="Tags" icon={<LabelIcon />} onClick={() => navigate("/tags")} />
                )}
                {admin?.role !== "CITY_ADMIN" && (
                  <BottomNavigationAction value="vendors" label="Vendors" icon={<StoreIcon />} onClick={() => navigate("/vendors")} />
                )}
                {admin?.role !== "CITY_ADMIN" && (
                  <BottomNavigationAction
                    value="batches"
                    label="Batches"
                    icon={
                      <Badge
                        variant="dot"
                        invisible={activeCount === 0}
                        sx={pulsingBadgeStyle}
                      >
                        <InventoryIcon />
                      </Badge>
                    }
                    onClick={() => navigate("/batches")}
                  />
                )}
                {admin?.role !== "CITY_ADMIN" && (
                  <BottomNavigationAction value="delivery" label="Delivery" icon={<LocalShippingIcon />} onClick={() => navigate("/delivery-boys")} />
                )}

              </BottomNavigation>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
}